import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ArrowRightIcon, MailIcon, LinkedinIcon, InstagramIcon, ChevronDownIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Home() {
  return <div className="w-full min-h-screen bg-white" data-id="element-1213">
      <Hero data-id="element-1214" />
      <Disciplines data-id="element-1215" />
      <Philosophy data-id="element-1216" />
      <Contact data-id="element-1217" />
    </div>;
}
function Hero() {
  const {
    scrollY
  } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const disciplines = [{
    name: 'Architecture',
    link: '/architecture'
  }, {
    name: 'UX/UI Design',
    link: '/ux-ui-design'
  }, {
    name: 'Art',
    link: '/art'
  }, {
    name: 'Creative Direction',
    link: '/creative-direction'
  }];
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white" data-id="element-1218">
      {/* Subtle Green Light Effect - From Edges */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-1219">
        <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" data-id="element-1220" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" data-id="element-1221" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" data-id="element-1222" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl" data-id="element-1223" />
      </div>
      {/* Geometric Accent Elements */}
      <motion.div style={{
      y
    }} className="absolute inset-0 pointer-events-none" data-id="element-1224">
        <div className="absolute top-0 right-0 w-96 h-96 border-l-2 border-b-2 border-emerald-500/10" style={{
        transform: 'rotate(45deg)'
      }} data-id="element-1225" />
        <div className="absolute bottom-0 left-0 w-80 h-80 border-r-2 border-t-2 border-emerald-500/10" style={{
        transform: 'rotate(45deg)'
      }} data-id="element-1226" />
        <div className="absolute top-1/3 left-1/4 w-64 h-64 border border-emerald-500/5" style={{
        transform: 'rotate(30deg)'
      }} data-id="element-1227" />
      </motion.div>
      <motion.div style={{
      opacity
    }} className="relative z-10 text-center px-6 max-w-6xl" data-id="element-1228">
        {/* Angular Accent Line */}
        <motion.div initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        duration: 1,
        delay: 0.5
      }} className="relative h-[2px] w-32 mx-auto mb-16" data-id="element-1229">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{
          clipPath: 'polygon(0 0, 100% 0, 95% 100%, 5% 100%)'
        }} data-id="element-1230" />
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-1231">
          <motion.h1 className="text-7xl md:text-8xl lg:text-[10rem] font-extralight tracking-tighter text-stone-900 mb-8 leading-[0.9]" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-1232">
            Maria Elina
          </motion.h1>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="space-y-6" data-id="element-1233">
          <p className="text-2xl md:text-3xl text-stone-600 font-light tracking-wide" data-id="element-1234">
            Multi-Faceted Creative & Product Manager
          </p>
          {/* Angular Discipline Tags */}
          <div className="flex flex-wrap justify-center gap-3 gap-y-8 mt-8" data-id="element-1235">
            {disciplines.map((discipline, i) => {
            const tagContent = <>
                  <div className="absolute inset-0 bg-emerald-500/10 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-300" data-id="element-1236" />
                  <span className="relative" data-id="element-1237">{discipline.name}</span>
                </>;
            const tagClasses = 'relative px-6 py-2 bg-transparent border border-emerald-500/30 text-sm text-emerald-600 font-light overflow-hidden group cursor-pointer';
            const tagStyle = {
              clipPath: 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)'
            };
            return <motion.span key={discipline.name} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.6 + i * 0.1
            }} data-id="element-1238">
                  {discipline.link ? <Link to={discipline.link} className={tagClasses} style={tagStyle} data-id="element-1239">
                      {tagContent}
                    </Link> : <span className={tagClasses} style={tagStyle} data-id="element-1240">
                      {tagContent}
                    </span>}
                </motion.span>;
          })}
          </div>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.8
      }} className="mt-12" data-id="element-1241">
          <Link to="/work" className="group inline-block relative" data-id="element-1242">
            {/* Outer border layer */}
            <div className="absolute inset-0 border-2 border-emerald-600 pointer-events-none" style={{
            clipPath: 'polygon(5% 0%, 100% 0%, 95% 100%, 0% 100%)'
          }} data-id="element-1243" />
            {/* Inner filled layer */}
            <div className="relative px-6 py-2 bg-emerald-600 text-white overflow-hidden mx-3 my-1.5" style={{
            clipPath: 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)'
          }} data-id="element-1244">
              <div className="absolute inset-0 bg-emerald-700 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-300" data-id="element-1245" />
              <span className="text-lg font-light relative z-10 inline-flex items-center gap-2" data-id="element-1246">
                Explore Work
                <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1247" />
              </span>
            </div>
          </Link>
        </motion.div>
        {/* Angular Accent Line */}
        <motion.div initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        duration: 1,
        delay: 1
      }} className="relative h-[2px] w-32 mx-auto mt-16" data-id="element-1248">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{
          clipPath: 'polygon(5% 0, 95% 0, 100% 100%, 0 100%)'
        }} data-id="element-1249" />
        </motion.div>
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 1.2
      }} className="mt-16 flex justify-center" data-id="element-1250">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="text-emerald-500" data-id="element-1251">
            <ChevronDownIcon className="w-8 h-8" data-id="element-1252" />
          </motion.div>
        </motion.div>
      </motion.div>
      {/* Angular Corner Accents */}
      <div className="absolute top-0 left-0 w-32 h-32 border-t-2 border-l-2 border-emerald-500/15" data-id="element-1253" />
      <div className="absolute top-0 right-0 w-32 h-32 border-t-2 border-r-2 border-emerald-500/15" data-id="element-1254" />
      <div className="absolute bottom-0 left-0 w-32 h-32 border-b-2 border-l-2 border-emerald-500/15" data-id="element-1255" />
      <div className="absolute bottom-0 right-0 w-32 h-32 border-b-2 border-r-2 border-emerald-500/15" data-id="element-1256" />
    </section>;
}
function Disciplines() {
  const disciplines = [{
    title: 'Creative Direction',
    description: 'Strategic vision that brings multidisciplinary projects to life with cohesion and impact',
    color: 'bg-stone-100',
    icon: '🌿',
    link: '/creative-direction'
  }, {
    title: 'Architecture',
    description: 'Spatial design and user planning that harmonizes form, function, heritage and innovation',
    color: 'bg-emerald-50',
    icon: '🏛️',
    link: '/architecture'
  }, {
    title: 'UX/UI Design',
    description: 'Human-centered digital experiences that blend aesthetics with intuitive interaction',
    color: 'bg-teal-50',
    icon: '✨',
    link: '/ux-ui-design'
  }, {
    title: 'Art',
    description: 'Visual expression and commissions focused on the human experience',
    color: 'bg-cyan-50',
    icon: '🎨',
    link: '/art'
  }];
  return <section className="relative py-32 px-6 bg-white overflow-hidden" data-id="element-1257">
      {/* Diagonal Section Divider */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-stone-50" style={{
      clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 100%)'
    }} data-id="element-1258" />
      {/* Angular Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]" data-id="element-1259">
        <div className="absolute inset-0" style={{
        backgroundImage: `
            linear-gradient(30deg, #059669 1px, transparent 1px),
            linear-gradient(-30deg, #059669 1px, transparent 1px)
          `,
        backgroundSize: '120px 120px'
      }} data-id="element-1260" />
      </div>
      <div className="max-w-7xl mx-auto relative" data-id="element-1261">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-24" data-id="element-1262">
          <div className="relative inline-block" data-id="element-1263">
            <h2 className="text-5xl md:text-6xl font-extralight text-stone-900 mb-6 tracking-tight" data-id="element-1264">
              Disciplines
            </h2>
            {/* Angular underline */}
            <motion.div initial={{
            scaleX: 0
          }} whileInView={{
            scaleX: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="absolute -bottom-2 left-0 right-0 h-[2px] bg-emerald-600" style={{
            clipPath: 'polygon(10% 0, 90% 0, 100% 100%, 0 100%)'
          }} data-id="element-1265" />
          </div>
          <p className="text-xl text-stone-600 font-light max-w-2xl mx-auto mt-8" data-id="element-1266">
            A holistic approach to design across multiple mediums
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1267">
          {disciplines.map((discipline, index) => <DisciplineCard key={discipline.title} discipline={discipline} index={index} data-id="element-1268" />)}
        </div>
      </div>
    </section>;
}
function DisciplineCard({
  discipline,
  index
}: {
  discipline: any;
  index: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const content = <>
      {/* Holographic gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/10 via-teal-400/10 to-cyan-400/10" data-id="element-1269" />
      <div className="absolute inset-0 bg-gradient-to-tr from-cyan-300/5 via-emerald-300/5 to-teal-300/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1270" />
      <div className="relative z-10 p-10 h-full flex flex-col" data-id="element-1271">
        <motion.div className="text-6xl mb-8" whileHover={{
        scale: 1.1,
        rotate: 5
      }} transition={{
        type: 'spring',
        stiffness: 300
      }} data-id="element-1272">
          {discipline.icon}
        </motion.div>
        <h3 className="text-3xl font-light text-stone-900 mb-6 tracking-tight" data-id="element-1273">
          {discipline.title}
        </h3>
        <p className="text-stone-700 font-light leading-relaxed mb-8 flex-grow" data-id="element-1274">
          {discipline.description}
        </p>
        <motion.div className="inline-flex items-center gap-2 text-stone-800 font-light group-hover:gap-3 transition-all" data-id="element-1275">
          <span data-id="element-1276">View Projects</span>
          <ArrowRightIcon className="w-4 h-4" data-id="element-1277" />
        </motion.div>
      </div>
      {/* Angular corner accents */}
      <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-emerald-600/0 group-hover:border-emerald-600/50 transition-colors duration-300" data-id="element-1278" />
      <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-emerald-600/0 group-hover:border-emerald-600/50 transition-colors duration-300" data-id="element-1279" />
    </>;
  const cardProps = {
    ref,
    initial: {
      opacity: 0,
      y: 50
    },
    animate: isInView ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 50
    },
    transition: {
      duration: 0.6,
      delay: index * 0.1
    },
    whileHover: {
      y: -8
    },
    className: 'group relative overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer h-full',
    style: {
      clipPath: 'polygon(0 0, 100% 0, 100% 95%, 95% 100%, 0 100%)'
    }
  };
  if (discipline.link) {
    return <Link to={discipline.link} className="h-full block" data-id="element-1280">
        <motion.div {...cardProps} data-id="element-1281">{content}</motion.div>
      </Link>;
  }
  return <motion.div {...cardProps} data-id="element-1282">{content}</motion.div>;
}
function Philosophy() {
  return <section className="relative py-20 px-6 bg-stone-50 overflow-hidden" data-id="element-1283">
      {/* Diagonal divider from previous section */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-white" style={{
      clipPath: 'polygon(0 0, 100% 50%, 100% 100%, 0 100%)'
    }} data-id="element-1284" />
      {/* Angular Grid Background */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]" data-id="element-1285">
        <div className="absolute inset-0" style={{
        backgroundImage: `
            linear-gradient(30deg, #059669 1px, transparent 1px),
            linear-gradient(-30deg, #059669 1px, transparent 1px)
          `,
        backgroundSize: '100px 100px'
      }} data-id="element-1286" />
      </div>
      {/* Geometric Accent Elements */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" data-id="element-1287">
        <div className="absolute top-20 right-20 w-96 h-96 border-2 border-emerald-600" style={{
        transform: 'rotate(45deg)'
      }} data-id="element-1288" />
        <div className="absolute bottom-20 left-20 w-80 h-80 border border-emerald-600" style={{
        transform: 'rotate(30deg)'
      }} data-id="element-1289" />
      </div>
      <div className="max-w-5xl mx-auto relative z-10" data-id="element-1290">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center" data-id="element-1291">
          <div className="relative inline-block mb-12" data-id="element-1292">
            {/* Angular Accent Line Above */}
            <motion.div initial={{
            scaleX: 0
          }} whileInView={{
            scaleX: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 1,
            delay: 0.2
          }} className="relative h-[2px] w-32 mx-auto mb-8" data-id="element-1293">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{
              clipPath: 'polygon(0 0, 100% 0, 95% 100%, 5% 100%)'
            }} data-id="element-1294" />
            </motion.div>
            <h2 className="text-5xl md:text-6xl font-extralight text-stone-900 tracking-tight" data-id="element-1295">
              Design Philosophy
            </h2>
            {/* Angular corner accents */}
            <div className="absolute -top-8 -right-8 w-16 h-16 border-t-2 border-r-2 border-emerald-500/30" data-id="element-1296" />
            <div className="absolute -bottom-8 -left-8 w-16 h-16 border-b-2 border-l-2 border-emerald-500/30" data-id="element-1297" />
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="relative" data-id="element-1298">
            <p className="text-lg text-stone-700 font-light leading-relaxed mb-12 px-8" data-id="element-1299">
              Inspired by human aspirations and business reality, my work
              bridges the gap between the trendy and the practical, the
              innovative and the comfortable, creating spaces and experiences
              that feel unique and welcoming.
            </p>
          </motion.div>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="text-2xl md:text-3xl text-stone-600 font-light leading-relaxed max-w-3xl mx-auto" data-id="element-1300">
            Every project is an opportunity to explore how thoughtful design can
            enhance well-being, foster connection, and create lasting impact
            across disciplines.
          </motion.p>
        </motion.div>
      </div>
      {/* Angular Corner Accents */}
      <div className="absolute top-32 left-0 w-32 h-32 border-t-2 border-l-2 border-emerald-500/20" data-id="element-1301" />
      <div className="absolute top-32 right-0 w-32 h-32 border-t-2 border-r-2 border-emerald-500/20" data-id="element-1302" />
      <div className="absolute bottom-8 left-0 w-32 h-32 border-b-2 border-l-2 border-emerald-500/20" data-id="element-1303" />
      <div className="absolute bottom-8 right-0 w-32 h-32 border-b-2 border-r-2 border-emerald-500/20" data-id="element-1304" />
    </section>;
}
function Contact() {
  return <section className="relative pt-0 pb-20 px-6 bg-stone-50 overflow-hidden" data-id="element-1305">
      {/* Diagonal divider */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-stone-50" style={{
      clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 50%)'
    }} data-id="element-1306" />
      {/* Angular Accent Line - At top edge */}
      <div className="relative z-10 pt-24" data-id="element-1307">
        <motion.div initial={{
        scaleX: 0
      }} whileInView={{
        scaleX: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 0.2
      }} className="relative h-[2px] w-32 mx-auto" data-id="element-1308">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{
          clipPath: 'polygon(0 0, 100% 0, 95% 100%, 5% 100%)'
        }} data-id="element-1309" />
        </motion.div>
      </div>
      {/* Subtle Green Light Effect */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-1310">
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" data-id="element-1311" />
      </div>
      {/* Angular grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]" data-id="element-1312">
        <div className="absolute inset-0" style={{
        backgroundImage: `
            linear-gradient(30deg, #059669 1px, transparent 1px),
            linear-gradient(-30deg, #059669 1px, transparent 1px)
          `,
        backgroundSize: '80px 80px'
      }} data-id="element-1313" />
      </div>
      {/* Geometric Accent Elements */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-1314">
        <div className="absolute top-1/4 right-1/4 w-64 h-64 border border-emerald-500/10" style={{
        transform: 'rotate(45deg)'
      }} data-id="element-1315" />
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 border-2 border-emerald-500/10" style={{
        transform: 'rotate(30deg)'
      }} data-id="element-1316" />
      </div>
      <div className="max-w-4xl mx-auto text-center relative z-10 pt-8" data-id="element-1317">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} data-id="element-1318">
          <h2 className="text-5xl md:text-6xl font-extralight mb-8 tracking-tight text-stone-900" data-id="element-1319">
            Let's Create Together
          </h2>
          <p className="text-xl text-stone-600 font-light mb-12 max-w-2xl mx-auto" data-id="element-1320">
            Open to opportunities and commissions across all design spheres.
          </p>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="mb-12" data-id="element-1321">
            <motion.a href="mailto:rassvet.hub@gmail.com" whileHover={{
            y: -3
          }} className="group inline-block relative" data-id="element-1322">
              {/* Outer border layer */}
              <div className="absolute inset-0 border-2 border-emerald-600 pointer-events-none" style={{
              clipPath: 'polygon(5% 0%, 100% 0%, 95% 100%, 0% 100%)'
            }} data-id="element-1323" />
              {/* Inner filled layer */}
              <div className="relative px-6 py-2 bg-emerald-600 text-white overflow-hidden mx-3 my-1.5" style={{
              clipPath: 'polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)'
            }} data-id="element-1324">
                <div className="absolute inset-0 bg-emerald-700 translate-y-[100%] group-hover:translate-y-0 transition-transform duration-300" data-id="element-1325" />
                <span className="text-lg font-light relative z-10 inline-flex items-center gap-2" data-id="element-1326">
                  <MailIcon className="w-4 h-4" data-id="element-1327" />
                  Get in Touch
                </span>
              </div>
            </motion.a>
          </motion.div>
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="flex justify-center gap-8 mb-16" data-id="element-1328">
            {[{
            icon: LinkedinIcon,
            href: 'https://www.linkedin.com/in/-maria--elina-'
          }, {
            icon: InstagramIcon,
            href: 'https://www.instagram.com/maria.spirals'
          }].map((social, index) => <motion.a key={index} href={social.href} whileHover={{
            scale: 1.2,
            y: -3
          }} className="text-stone-600 hover:text-emerald-600 transition-colors duration-300" data-id="element-1329">
                <social.icon className="w-6 h-6" data-id="element-1330" />
              </motion.a>)}
          </motion.div>
          {/* Angular Accent Line */}
          <motion.div initial={{
          scaleX: 0
        }} whileInView={{
          scaleX: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          delay: 0.8
        }} className="relative h-[2px] w-32 mx-auto mb-8" data-id="element-1331">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{
            clipPath: 'polygon(5% 0, 95% 0, 100% 100%, 0 100%)'
          }} data-id="element-1332" />
          </motion.div>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 1
        }} className="text-stone-600 font-light text-sm" data-id="element-1333">
            © 2024 Maria Elina. All rights reserved.
          </motion.p>
        </motion.div>
      </div>
      {/* Angular corner accents */}
      <div className="absolute bottom-0 left-0 w-32 h-32 border-b-2 border-l-2 border-emerald-500/30" data-id="element-1334" />
      <div className="absolute bottom-0 right-0 w-32 h-32 border-b-2 border-r-2 border-emerald-500/30" data-id="element-1335" />
      <div className="absolute top-32 left-0 w-24 h-24 border-t-2 border-l-2 border-emerald-500/20" data-id="element-1336" />
      <div className="absolute top-32 right-0 w-24 h-24 border-t-2 border-r-2 border-emerald-500/20" data-id="element-1337" />
    </section>;
}